import React, { useEffect, useState } from "react";
import { Container, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import HashtagForm from "./HashtagForm";
import GeneratedResponses from "./GeneratedResponses";
import { generateCaptionAndHashtagsAiResponse } from "../../services/getAiResponse";
import { socialMediaOptions, hashtagCountOptions } from "./constants";
import {
  AiResponse,
  GeneratedResponse,
  SocialMediaOption,
  HashtagCountOption,
} from "./types";
import { useGeneratedResponsesStore } from "./store"; // Import the store
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";
import { Box } from "./hashtagGeneratorStyle";

const HashtagGenerator: React.FC = () => {
  const [image, setImage] = useState<File | undefined>(undefined);
  const [topic, setTopic] = useState<string>("");
  const [language, setLanguage] = useState<string>("English");
  const [platform, setPlatform] = useState<SocialMediaOption | null>(
    socialMediaOptions[0]
  );
  const [hashtagCount, setHashtagCount] = useState<HashtagCountOption | null>(
    hashtagCountOptions[0]
  );
  const [format, setFormat] = useState<string>("One line");
  const [writingTone, setWritingTone] = useState<string>("Formal");
  const [captionValue, setCaptionValue] = useState<number>(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [showToast, setShowToast] = useState<boolean>(false);
  const [showResults, setShowResults] = useState<boolean>(false);

  const generatedResponses = useGeneratedResponsesStore(
    (state) => state.generatedResponses
  );
  const addResponse = useGeneratedResponsesStore((state) => state.addResponse);

  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [generatedResponses]);

  const handleGenerate = async () => {
    if (!topic || !platform || !hashtagCount) {
      alert(
        "Please fill in all required fields (Topic, Social Media, Generate)."
      );
      return;
    }

    setLoading(true);
    try {
      const numberOfHashtags = hashtagCount.value;
      const response: AiResponse = await generateCaptionAndHashtagsAiResponse(
        topic,
        language,
        platform.value,
        numberOfHashtags,
        captionValue,
        writingTone,
        image
      );

      const { caption, hashtags, captionList } = response;
      let cleanedHashtags: string[] = Array.isArray(hashtags)
        ? hashtags.flatMap((h) =>
            h.includes(" ")
              ? h.split(" ").filter((item) => item.startsWith("#"))
              : h
          )
        : hashtags.split(" ").filter((h) => h.startsWith("#"));

      cleanedHashtags = cleanedHashtags.slice(0, numberOfHashtags);

      const newResponse: GeneratedResponse = {
        id: Date.now(),
        caption,
        captionList,
        hashtags: cleanedHashtags,
        platform: platform.value,
        writingTone,
        timestamp: new Date().toLocaleString(),
      };

      addResponse(newResponse);
      setShowResults(true);
      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (error) {
      console.error("Error generating caption and hashtags:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyResponse = (response: GeneratedResponse) => {
    const textToCopy = `${response.caption}\n\n${
      format === "Numbered list"
        ? response.hashtags.map((h, i) => `${i + 1}. ${h}`).join("\n")
        : response.hashtags.join(" ")
    }`;

    navigator.clipboard.writeText(textToCopy).then(() => setShowToast(true));
  };

  const handleExportResults = () => {
    if (!generatedResponses.length) {
      alert("Nothing to export yet!");
      return;
    }

    const exportText = generatedResponses
      .map(
        (res) =>
          `${res.caption}\n\n${
            format === "Numbered list"
              ? res.hashtags.map((h, i) => `${i + 1}. ${h}`).join("\n")
              : res.hashtags.join(" ")
          }\n---`
      )
      .join("\n");

    const blob = new Blob([exportText], { type: "text/plain" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "generated_caption_and_hashtags.txt";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Container
        className={`${
          !isInIframe ? "mt-5" : "interDisplay"
        } position-relative p-0`}
      >
        <Row className="justify-content-center">
          <Col md={10}>
            {!isInIframe && (
              <ToolsHeader
                title="Generate Hashtags and Captions"
                description="Generate catchy captions and trending hashtags to boost
                engagement!"
              />
            )}
            <HashtagForm
              image={image}
              setImage={setImage}
              topic={topic}
              setTopic={setTopic}
              language={language}
              setLanguage={setLanguage}
              platform={platform}
              setPlatform={setPlatform}
              hashtagCount={hashtagCount}
              setHashtagCount={setHashtagCount}
              format={format}
              setFormat={setFormat}
              writingTone={writingTone}
              setWritingTone={setWritingTone}
              captionValue={captionValue}
              setCaptionValue={setCaptionValue}
              handleGenerate={handleGenerate}
              handleExportResults={handleExportResults}
              loading={loading}
            />
          </Col>
        </Row>
        {showResults && generatedResponses.length > 0 && (
          <Row className="justify-content-center mt-4">
            <Col md={10}>
              <GeneratedResponses
                generatedResponses={generatedResponses}
                format={format}
                handleCopyResponse={handleCopyResponse}
              />
            </Col>
          </Row>
        )}
        <ToastContainer
          position="top-end"
          className="p-3"
          style={{ zIndex: 1050 }}
        >
          <Toast
            onClose={() => setShowToast(false)}
            show={showToast}
            delay={3000}
            autohide
            bg="success"
          >
            <Toast.Header>
              <strong className="me-auto">Success</strong>
            </Toast.Header>
            <Toast.Body className="text-white">
              Response copied to clipboard!
            </Toast.Body>
          </Toast>
        </ToastContainer>
      </Container>
    </Box>
  );
};

export default HashtagGenerator;